import { __extends } from "tslib";
import { EmptyValidator } from '../Validation/Validators/IPropertyValidator';
import PropertyInfo from './PropertyInfo';
import { Utils } from '../Utils';
var PropertyInstance = /** @class */ (function () {
    function PropertyInstance(attachedTo, propertyInfo, isObservable) {
        if (isObservable === void 0) { isObservable = true; }
        this.propertyInfo = propertyInfo;
        this.isObservable = isObservable;
        if (attachedTo)
            this.attachedTo = attachedTo;
        this.propertyName = propertyInfo.propertyName;
        if (!isObservable) {
            this._validator = EmptyValidator;
        }
    }
    Object.defineProperty(PropertyInstance.prototype, "value", {
        get: function () {
            return this.attachedTo[this.propertyName];
        },
        set: function (newVal) {
            this.attachedTo[this.propertyName] = newVal;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PropertyInstance.prototype, "uniqueID", {
        get: function () {
            return (this.attachedTo ? (this.attachedTo.entityIdentifier + "!") : "") + this.propertyName;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PropertyInstance.prototype, "peek", {
        /** Gets the value of the property without registering a dependency */
        get: function () {
            if (this.isObservable) {
                return Utils.peek(this.attachedTo, this.propertyName);
            }
            else {
                return this.value;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PropertyInstance.prototype, "validator", {
        get: function () {
            if (!this._validator) {
                if (this.propertyInfo.rules.length) {
                    this._validator = this.attachedTo.validator.createPropertyValidator(this);
                }
                else {
                    this._validator = EmptyValidator;
                }
            }
            return this._validator;
        },
        enumerable: false,
        configurable: true
    });
    return PropertyInstance;
}());
export default PropertyInstance;
var uniqueIdSymbol = Symbol("uniqueId");
/**
 * Allows a property declared on a non model base class to be wrapped, and passed as a bind property.
 */
var PropertyAccessor = /** @class */ (function () {
    function PropertyAccessor(attachedTo, propertyInfo) {
        this.attachedTo = attachedTo;
        this.propertyInfo = propertyInfo;
        this.propertyName = propertyInfo.propertyName;
    }
    Object.defineProperty(PropertyAccessor.prototype, "uniqueID", {
        get: function () {
            return this.attachedTo[uniqueIdSymbol] + "!" + this.propertyName;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PropertyAccessor.prototype, "validator", {
        get: function () {
            return EmptyValidator;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PropertyAccessor.prototype, "value", {
        get: function () {
            return this.attachedTo[this.propertyName];
        },
        set: function (newVal) {
            this.attachedTo[this.propertyName] = newVal;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PropertyAccessor.prototype, "peek", {
        get: function () {
            return this.attachedTo[this.propertyName];
        },
        enumerable: false,
        configurable: true
    });
    PropertyAccessor.create = function (model, property) {
        // @ts-ignore
        if (!model[uniqueIdSymbol]) {
            // @ts-ignore
            model[uniqueIdSymbol] = Utils.uuidv4();
        }
        return new PropertyAccessor(model, new PropertyInfo(property));
    };
    return PropertyAccessor;
}());
export { PropertyAccessor };
var TemporaryProperty = /** @class */ (function (_super) {
    __extends(TemporaryProperty, _super);
    function TemporaryProperty(attachedTo, valueAccessor, options) {
        var _this = this;
        var _a, _b;
        var propertyInfo = new PropertyInfo((_a = options === null || options === void 0 ? void 0 : options.name) !== null && _a !== void 0 ? _a : Utils.uuidv4());
        propertyInfo.displayName = (_b = options === null || options === void 0 ? void 0 : options.label) !== null && _b !== void 0 ? _b : "";
        propertyInfo.isReadOnly = (options === null || options === void 0 ? void 0 : options.onChanged) === undefined;
        _this = _super.call(this, attachedTo, propertyInfo) || this;
        _this.valueAccessor = valueAccessor;
        _this.onChanged = options === null || options === void 0 ? void 0 : options.onChanged;
        return _this;
    }
    Object.defineProperty(TemporaryProperty.prototype, "value", {
        get: function () {
            return this.valueAccessor();
        },
        set: function (value) {
            if (this.onChanged) {
                this.onChanged(value);
            }
            else {
                throw Error("Cannot set value of temporary property ".concat(this.propertyInfo.propertyName));
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TemporaryProperty.prototype, "peek", {
        get: function () {
            return this.value;
        },
        enumerable: false,
        configurable: true
    });
    return TemporaryProperty;
}(PropertyAccessor));
export { TemporaryProperty };
/**
 * Creates a temporary property if you need a neo editor to show a computed value, and cannot use the meta object on a neo model.
 * The property should be attached to a component class instance, and the property should be passed as the editors `bind` prop.
 *
 * E.g.
 * In component class:
 * ```typescript
 * this.myProperty = createProperty(this, ...);
 * ```
 * In render method:
 * ```jsx
 * <Neo.FormGroup bind={this.myProperty} />
 * ```
 * @param host The instance this property is stored on, usually `this`.
 * @param valueAccessor Function that returns the property value.
 */
export function createProperty(host, valueAccessor, options) {
    return new TemporaryProperty(host, valueAccessor, options);
}
